import React, { useState, useEffect } from 'react';
import { FlattenedInvoiceData } from '../../types/invoice';
import { formatCurrency } from '../../utils/formatters';
import { FieldConfig, fieldSections } from '../../config/invoiceFields';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from '../../hooks/useTranslation';
import { useLanguage } from '../../contexts/LanguageContext';
import { AlertCircle, Info, CheckCircle, AlertTriangle } from 'lucide-react';
import { ValidationResult, ValidationError } from '../../utils/invoiceValidation';
import InvoiceTypeSelector from '../common/InvoiceTypeSelector';
import TaxCategorySelector from '../common/TaxCategorySelector';
import UnitCodeSelector from '../common/UnitCodeSelector';
import PaymentMeansSelector from '../common/PaymentMeansSelector';
import CountrySelector from '../common/CountrySelector';
import CurrencySelector from '../common/CurrencySelector';
import { TaxSubtotalsDisplay } from './TaxSubtotalsDisplay';

interface InvoiceViewerProps {
    invoiceData: FlattenedInvoiceData | null;
    readOnly?: boolean;
    onDataChange?: (key: string, value: string | number) => void;
    onAddInvoiceLine?: () => void;
    onDeleteInvoiceLine?: (index: number) => void;
    showValidation?: boolean;
    validationResult?: ValidationResult | null;
    fieldErrors?: Record<string, ValidationError>;
    isValidating?: boolean;
}


const DATE_FIELDS = [
    'IssueDate',
    'DueDate',
    'TaxPointDate',
    'ActualDeliveryDate',
] as const;

type DateField = typeof DATE_FIELDS[number];

interface TextValue {
    '#text': string;
    '@_unitCode'?: string;
}

const getSeparatorsByLanguage = (language: string) => {
    return language.startsWith('de') 
        ? { decimalSeparator: ',', groupSeparator: '.' }
        : { decimalSeparator: '.', groupSeparator: ',' };
};


const InvoiceViewer: React.FC<InvoiceViewerProps> = ({ 
    invoiceData, 
    readOnly = true, 
    onDataChange, 
    onAddInvoiceLine, 
    onDeleteInvoiceLine,
    showValidation = false,
    validationResult,
    fieldErrors = {},
    isValidating = false
}) => {
    const { t } = useTranslation();
    const { language } = useLanguage();
    const separators = getSeparatorsByLanguage(language);
    
    // Helper function to translate validation messages
    const translateValidationMessage = (message: string): string => {
        // For German, directly return German text if language is 'de'
        if (language === 'de') {
            const germanMessages: Record<string, string> = {
                'Seller electronic address (e.g., email) is required (BR-DE-2)': 'Elektronische Adresse des Verkäufers (z.B. E-Mail) ist erforderlich (BR-DE-2)',
                'Buyer electronic address must be provided (PEPPOL-EN16931-R010)': 'Die elektronische Adresse des Käufers muss angegeben werden (PEPPOL-EN16931-R010)',
                'Payment instructions are required for XRechnung compliance (BR-DE-1)': 'Zahlungsanweisungen sind für die XRechnung-Konformität erforderlich (BR-DE-1)',
                'Seller contact telephone number is required (BR-DE-6)': 'Telefonnummer des Verkäuferkontakts ist erforderlich (BR-DE-6)',
                'Seller contact email address is required (BR-DE-7)': 'E-Mail-Adresse des Verkäuferkontakts ist erforderlich (BR-DE-7)',
                'Seller contact point is required (BR-DE-5)': 'Ansprechpartner des Verkäufers ist erforderlich (BR-DE-5)',
                'Seller city is required (BR-DE-3)': 'Stadt des Verkäufers ist erforderlich (BR-DE-3)',
                'Seller postal code is required (BR-DE-4)': 'Postleitzahl des Verkäufers ist erforderlich (BR-DE-4)',
                'Buyer city is required (BR-DE-8)': 'Stadt des Käufers ist erforderlich (BR-DE-8)',
                'Buyer postal code is required (BR-DE-9)': 'Postleitzahl des Käufers ist erforderlich (BR-DE-9)',
            };
            if (germanMessages[message]) {
                return germanMessages[message];
            }
        }
        
        // Map of English messages to translation keys
        const messageTranslationMap: Record<string, string> = {
            'Invoice number is required': 'validation.invoiceNumberRequired',
            'Issue date is required': 'validation.issueDateRequired',
            'Invalid date format. Use YYYY-MM-DD': 'validation.invalidDateFormat',
            'Issue date cannot be in the future': 'validation.issueDateFuture',
            'Invoice type code is required': 'validation.invoiceTypeRequired',
            'Currency code is required': 'validation.currencyCodeRequired',
            'Due date cannot be before issue date': 'validation.dueDateBeforeIssue',
            'Seller name is required': 'validation.sellerNameRequired',
            'Seller address is required': 'validation.sellerAddressRequired',
            'Seller country is required': 'validation.sellerCountryRequired',
            'Buyer name is required': 'validation.buyerNameRequired',
            'Buyer electronic address must be provided (PEPPOL-EN16931-R010)': 'validation.buyerElectronicAddressRequired',
            'Buyer country is required': 'validation.buyerCountryRequired',
            'Tax category is required': 'validation.taxCategoryRequired',
            'Tax percentage is required for standard rate': 'validation.taxPercentRequired',
            'Tax percentage must be between 0 and 100': 'validation.taxPercentRange',
            'Seller contact telephone number is required (BR-DE-6)': 'validation.sellerTelephoneRequired',
            'Seller contact email address is required (BR-DE-7)': 'validation.sellerEmailRequired',
            'Seller contact point is required (BR-DE-5)': 'validation.sellerContactRequired',
            'Payment instructions are required for XRechnung compliance (BR-DE-1)': 'validation.paymentInstructionsRequired',
            'Seller city is required (BR-DE-3)': 'validation.sellerCityRequired',
            'Seller postal code is required (BR-DE-4)': 'validation.sellerPostalCodeRequired',
            'Buyer city is required (BR-DE-8)': 'validation.buyerCityRequired',
            'Buyer postal code is required (BR-DE-9)': 'validation.buyerPostalCodeRequired',
            'Email should contain exactly one @ symbol with valid format (BR-DE-28)': 'validation.emailFormatInvalid',
            'Telephone number should contain at least 3 digits (BR-DE-27)': 'validation.telephoneFormatInvalid',
        };

        const translationKey = messageTranslationMap[message];
        if (translationKey) {
            return t(translationKey);
        }
        
        // Return original message if no translation found
        return message;
    };
    const [expandedRows, setExpandedRows] = useState<number[]>([]);
    
    // Get validation error for a specific field
    const getFieldError = (fieldKey: string): ValidationError | null => {
        // Check field-specific errors first
        if (fieldErrors[fieldKey]) {
            return fieldErrors[fieldKey];
        }
        
        // Check validation result for field-specific errors
        if (validationResult && showValidation) {
            const allErrors = [
                ...validationResult.errors,
                ...validationResult.warnings,
                ...validationResult.info
            ];
            return allErrors.find(error => error.field === fieldKey) || null;
        }
        
        return null;
    };
    
    // Validation icon function
    const getValidationIcon = (fieldKey: string) => {
        const error = getFieldError(fieldKey);
        if (!error || !showValidation) return null;
        
        const IconComponent = error.severity === 'error' ? AlertCircle :
                            error.severity === 'warning' ? AlertTriangle :
                            Info;
        
        return (
            <div title={error.message} className="flex-shrink-0">
                <IconComponent 
                    className={`w-4 h-4 ${
                        error.severity === 'error' ? 'text-error' : 
                        error.severity === 'warning' ? 'text-warning' : 
                        'text-info'
                    }`}
                />
            </div>
        );
    };
    
    // Handle field change
    const handleFieldChange = (key: string, value: string | number | any) => {
        if (onDataChange) {
            onDataChange(key, value);
        }
    };

    useEffect(() => {
        // Only reset expanded rows when the number of invoice lines changes
        const currentLineCount = invoiceData?.InvoiceLines?.length ?? 0;
        setExpandedRows(prev => {
            // Keep only valid indices
            return prev.filter(index => index < currentLineCount);
        });
    }, [invoiceData?.InvoiceLines?.length]);

    if (!invoiceData) {
        return <p className="text-text-primary">No data available.</p>;
    }

    const toggleRow = (index: number) => {
        setExpandedRows(prev =>
            prev.includes(index)
                ? prev.filter(i => i !== index)
                : [...prev, index]
        );
    };

    const formatValue = (value: any, format?: string): string => {
        if (value === null || value === undefined || value === '') return '';

        switch (format) {
            case 'currency':
                return formatCurrency(value, invoiceData.DocumentCurrencyCode || 'EUR');
            case 'number':
                return new Intl.NumberFormat('de-DE', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }).format(Number(value));
            default:
                return String(value);
        }
    };

    const renderLabelWithInfo = (field: FieldConfig) => (
        <div className="flex items-center gap-2">
            <span className="field-label">{t(field.labelKey)}</span>
            <div className="relative group">
                <svg 
                    className="w-4 h-4 text-text-muted hover:text-text-primary cursor-help transition-colors -translate-y-px"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M7.5 0.875C3.84375 0.875 0.875 3.84375 0.875 7.5C0.875 11.1562 3.84375 14.125 7.5 14.125C11.1562 14.125 14.125 11.1562 14.125 7.5C14.125 3.84375 11.1562 0.875 7.5 0.875ZM7.5 3.5C7.91562 3.5 8.25 3.83437 8.25 4.25C8.25 4.66563 7.91562 5 7.5 5C7.08437 5 6.75 4.66563 6.75 4.25C6.75 3.83437 7.08437 3.5 7.5 3.5ZM9 11H6C5.72375 11 5.5 10.7762 5.5 10.5C5.5 10.2238 5.72375 10 6 10H6.75V7H6.5C6.22375 7 6 6.77625 6 6.5C6 6.22375 6.22375 6 6.5 6H7.5C7.77625 6 8 6.22375 8 6.5V10H8.75C9.02625 10 9.25 10.2238 9.25 10.5C9.25 10.7762 9.02625 11 8.75 11H9Z"
                        fill="currentColor"
                    />
                </svg>
                <div className="absolute left-full top-0 ml-2 px-4 py-2 bg-bg-secondary text-text-primary text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-md min-w-[200px] max-w-[300px] z-10">
                    {t(field.descriptionKey)}
                </div>
            </div>
        </div>
    );

    const renderLineItemLabelWithInfo = (labelKey: string, descriptionKey: string) => (
        <div className="flex items-center gap-2">
            <span className="field-label">{t(labelKey)}</span>
            <div className="relative group">
                <svg 
                    className="w-4 h-4 text-text-muted hover:text-text-primary cursor-help transition-colors"
                    viewBox="0 0 15 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M7.5 0.875C3.84375 0.875 0.875 3.84375 0.875 7.5C0.875 11.1562 3.84375 14.125 7.5 14.125C11.1562 14.125 14.125 11.1562 14.125 7.5C14.125 3.84375 11.1562 0.875 7.5 0.875ZM7.5 3.5C7.91562 3.5 8.25 3.83437 8.25 4.25C8.25 4.66563 7.91562 5 7.5 5C7.08437 5 6.75 4.66563 6.75 4.25C6.75 3.83437 7.08437 3.5 7.5 3.5ZM9 11H6C5.72375 11 5.5 10.7762 5.5 10.5C5.5 10.2238 5.72375 10 6 10H6.75V7H6.5C6.22375 7 6 6.77625 6 6.5C6 6.22375 6.22375 6 6.5 6H7.5C7.77625 6 8 6.22375 8 6.5V10H8.75C9.02625 10 9.25 10.2238 9.25 10.5C9.25 10.7762 9.02625 11 8.75 11H9Z"
                        fill="currentColor"
                    />
                </svg>
                <div className="absolute left-full top-0 ml-2 px-4 py-2 bg-bg-secondary text-text-primary text-sm rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-md min-w-[200px] max-w-[300px] z-10">
                    {t(descriptionKey)}
                </div>
            </div>
        </div>
    );

    const renderField = (field: FieldConfig) => {
        const value = invoiceData[field.key];
        const fieldError = getFieldError(field.key);
        const hasError = fieldError && showValidation;

        // Common field container class for consistent styling
        const fieldContainerClass = "grid grid-cols-2 gap-4 py-3 border-b border-border-secondary hover:bg-bg-secondary/30 rounded-sm transition-colors";
        
        // Input styling with validation
        const getInputClassName = (baseClass: string) => {
            let className = baseClass;
            if (hasError) {
                switch (fieldError.severity) {
                    case 'error':
                        className += ' border-error focus:border-error';
                        break;
                    case 'warning':
                        className += ' border-warning focus:border-warning';
                        break;
                    case 'info':
                        className += ' border-info focus:border-info';
                        break;
                }
            }
            return className;
        };

        // Handle dropdown fields with custom components
        if (field.key === 'InvoiceTypeCode' && !readOnly) {
            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        <InvoiceTypeSelector
                            value={String(value || '')}
                            onChange={(newValue) => handleFieldChange(field.key, newValue)}
                            placeholder={t('invoiceFields.InvoiceTypeCode.selectPlaceholder', { defaultValue: 'Select invoice type...' })}
                            disabled={readOnly}
                            error={!!hasError}
                            showGermanNames={language === 'de'}
                            data-testid={`field-${field.key}`}
                        />
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        if (field.key === 'DocumentCurrencyCode' && !readOnly) {
            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        <CurrencySelector
                            value={String(value || '')}
                            onChange={(newValue) => handleFieldChange(field.key, newValue)}
                            placeholder={t('invoiceFields.DocumentCurrencyCode.selectPlaceholder', { defaultValue: 'Select currency...' })}
                            disabled={readOnly}
                            error={!!hasError}
                            data-testid={`field-${field.key}`}
                        />
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        if ((field.key === 'Seller.CountryCode' || field.key === 'Buyer.CountryCode') && !readOnly) {
            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        <CountrySelector
                            value={String(value || '')}
                            onChange={(newValue) => handleFieldChange(field.key, newValue)}
                            placeholder={t(`invoiceFields.${field.key}.selectPlaceholder`, { defaultValue: 'Select country...' })}
                            disabled={readOnly}
                            error={!!hasError}
                            data-testid={`field-${field.key}`}
                        />
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        if ((field.key === 'Tax.Category' || field.key.includes('TaxCategory')) && !readOnly) {
            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        <TaxCategorySelector
                            value={String(value || '')}
                            onChange={(newValue) => handleFieldChange(field.key, newValue)}
                            placeholder={t(`invoiceFields.${field.key}.selectPlaceholder`, { defaultValue: 'Select tax category...' })}
                            disabled={readOnly}
                            error={!!hasError}
                            showGermanNames={language === 'de'}
                            data-testid={`field-${field.key}`}
                        />
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        // Special handling for TaxSubtotals array
        if (field.key === 'TaxSubtotals') {
            const subtotals = (Array.isArray(value) ? value : []) as Array<{
                TaxableAmount: number;
                TaxAmount: number;
                TaxCategory: {
                    ID: string;
                    Percent: number;
                    TaxScheme: {
                        ID: string;
                    };
                };
            }>;

            return (
                <div key={field.key} className="col-span-2">
                    <TaxSubtotalsDisplay
                        subtotals={subtotals}
                        readOnly={readOnly}
                        onChange={(newSubtotals) => handleFieldChange(field.key, newSubtotals)}
                        onAddSubtotal={() => {
                            const newSubtotal = {
                                TaxableAmount: 0,
                                TaxAmount: 0,
                                TaxCategory: {
                                    ID: 'S',
                                    Percent: 19,
                                    TaxScheme: {
                                        ID: 'VAT'
                                    }
                                }
                            };
                            handleFieldChange(field.key, [...subtotals, newSubtotal]);
                        }}
                        onDeleteSubtotal={(index) => {
                            const newSubtotals = subtotals.filter((_, i) => i !== index);
                            handleFieldChange(field.key, newSubtotals);
                        }}
                        currency={invoiceData.DocumentCurrencyCode || 'EUR'}
                        totalTaxAmount={Number(invoiceData['Tax.Amount']) || 0}
                        showValidation={showValidation}
                    />
                </div>
            );
        }

        if ((field.key === 'Payment.Type' || field.key.includes('PaymentMeans')) && !readOnly) {
            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        <PaymentMeansSelector
                            value={String(value || '')}
                            onChange={(newValue) => handleFieldChange(field.key, newValue)}
                            placeholder={t(`invoiceFields.${field.key}.selectPlaceholder`, { defaultValue: 'Select payment method...' })}
                            disabled={readOnly}
                            error={!!hasError}
                            showGermanNames={language === 'de'}
                            data-testid={`field-${field.key}`}
                        />
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        if (field.format === 'currency') {
            const numericValue = (() => {
                if (typeof value === 'object' && value !== null && '#text' in value) {
                    return (value as TextValue)['#text'];
                }
                if (typeof value === 'number') {
                    return value;
                }
                return 0;
            })();

            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        {readOnly ? (
                            <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                {formatValue(value, field.format)}
                            </span>
                        ) : (
                            <CurrencyInput
                                id={field.key}
                                name={field.key}
                                placeholder="0,00"
                                defaultValue={numericValue}
                                decimalsLimit={2}
                                decimalSeparator={separators.decimalSeparator}
                                groupSeparator={separators.groupSeparator}
                                onValueChange={(value) => {
                                    handleFieldChange(field.key, value || '0');
                                }}
                                className={getInputClassName("px-3 py-2 rounded border border-border-primary focus:border-brand-primary transition-colors w-full")}
                                disabled={readOnly}
                                intlConfig={{ locale: language, currency: invoiceData.DocumentCurrencyCode || 'EUR' }}
                                allowNegativeValue={false}
                            />
                        )}
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        if (DATE_FIELDS.includes(field.key as DateField)) {
            const dateValue = value 
                ? (typeof value === 'string' || typeof value === 'number')
                    ? new Date(value).toISOString().split('T')[0]
                    : ''
                : '';

            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        {readOnly ? (
                            <span 
                                className={`readonly-field px-3 py-2 rounded min-h-[36px] flex items-center border border-border-primary ${
                                    dateValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
                                }`}
                                title={t(field.descriptionKey)}
                            >
                                {dateValue || '\u00A0'}
                            </span>
                        ) : (
                            <input
                                type="date"
                                value={dateValue}
                                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                className={getInputClassName(`px-3 py-2 rounded min-h-[36px] border border-border-primary focus:border-brand-primary transition-colors w-full
                                    [&::-webkit-calendar-picker-indicator]:filter-calendar
                                    [&::-webkit-calendar-picker-indicator]:hover:opacity-70
                                    [&::-webkit-calendar-picker-indicator]:cursor-pointer
                                    ${dateValue ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] placeholder:text-[var(--text-muted)] empty-date-field'}`)}
                                title={t(field.descriptionKey)}
                                placeholder="dd.mm.yyyy"
                            />
                        )}
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }


        if (readOnly) {
            if (field.key === 'Notes.Note') {
                return (
                    <div key={field.key} className={fieldContainerClass}>
                        {renderLabelWithInfo(field)}
                        <div 
                            className="readonly-field px-3 py-2 rounded whitespace-pre overflow-auto border border-border-primary md-scrollbar"
                            title={t(field.descriptionKey)}
                        >
                            {formatValue(value, field.format) || '\u00A0'}
                        </div>
                    </div>
                );
            }

            return (
                <div key={field.key} className={fieldContainerClass}>
                        {renderLabelWithInfo(field)}
                        <span
                            className="readonly-field px-3 py-2 rounded min-h-[36px] overflow-auto border border-border-primary md-scrollbar"
                            title={t(field.descriptionKey)}
                        >
                            {formatValue(value, field.format) || '\u00A0'}
                        </span>
                    </div>
                );
        }

        if (field.key === 'Notes.Note') {
            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        <textarea
                            value={formatValue(value, field.format)}
                            onChange={(e) => handleFieldChange(field.key, e.target.value)}
                            className={getInputClassName("px-3 py-2 rounded resize-none overflow-auto border border-border-primary focus:border-brand-primary transition-colors md-scrollbar w-full")}
                            title={t(field.descriptionKey)}
                            rows={15}
                        />
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        if (field.format === 'multiline') {
            const stringValue = Array.isArray(value) || typeof value === 'object'
                ? JSON.stringify(value, null, 2)
                : String(value || '');

            return (
                <div key={field.key} className={fieldContainerClass}>
                    <div className="flex items-center space-x-2">
                        {renderLabelWithInfo(field)}
                        {getValidationIcon(field.key)}
                    </div>
                    <div>
                        {readOnly ? (
                            <div 
                                className="readonly-field px-3 py-2 rounded whitespace-pre-wrap border border-border-primary md-scrollbar"
                                title={t(field.descriptionKey)}
                            >
                                {stringValue || '\u00A0'}
                            </div>
                        ) : (
                            <textarea
                                value={stringValue}
                                onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                className={getInputClassName("px-3 py-2 rounded min-h-[100px] resize-y border border-border-primary focus:border-brand-primary transition-colors md-scrollbar w-full")}
                                title={t(field.descriptionKey)}
                            />
                        )}
                        {hasError && (
                            <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                                {fieldError.message}
                            </p>
                        )}
                    </div>
                </div>
            );
        }

        return (
            <div key={field.key} className={fieldContainerClass}>
                <div className="flex items-center space-x-2">
                    {renderLabelWithInfo(field)}
                    {getValidationIcon(field.key)}
                </div>
                <div>
                    <input
                        type="text"
                        value={formatValue(value, field.format)}
                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                        className={getInputClassName("px-3 py-2 rounded min-h-[36px] overflow-auto border border-border-primary focus:border-brand-primary transition-colors md-scrollbar w-full")}
                        title={t(field.descriptionKey)}
                    />
                    {hasError && (
                        <p className={`text-xs mt-1 ${fieldError.severity === 'error' ? 'text-error' : fieldError.severity === 'warning' ? 'text-warning' : 'text-info'}`}>
                            {fieldError.message}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    const renderInvoiceLines = () => {
        if (!invoiceData.InvoiceLines?.length) {
            return null;
        }

        return (
            <div className="mb-8">
                <h2 className="md-title-large text-text-primary mb-4 border-l-4 border-brand-primary pl-3 md-padding-2 bg-brand-primary/10 rounded-r-md shadow-sm dark:border-brand-primary">{t('sections.lineItems')}</h2>
                <div className="md-card md-card-2 md-padding-4 overflow-x-hidden border border-border-secondary rounded-lg">
                    {/* Header */}
                    <div className="grid grid-cols-4 gap-4 px-4 py-3 md-label-large text-text-primary border-b border-border-secondary bg-bg-tertiary rounded-t-lg shadow-sm">
                        <div className="truncate font-medium">Description</div>
                        <div className="text-right font-medium">Quantity</div>
                        <div className="text-right font-medium">Unit Price</div>
                        <div className="text-right font-medium">Net Amount</div>
                    </div>
                    {/* Invoice lines */}
                    <div className="space-y-3 mt-3">
                        {invoiceData.InvoiceLines.map((line, index) => (
                            <div key={index} className="md-card overflow-hidden md-duration-medium">
                                {/* Main Row */}
                                <div
                                    className={`grid grid-cols-4 gap-4 px-4 py-3 cursor-pointer md-standard-easing
                                        hover:bg-hover-bg active:bg-active-bg transition-colors rounded-md
                                        ${expandedRows.includes(index) ? 'bg-focus-bg elevation-1 border-l-2 border-brand-primary' : 'hover:elevation-1'}`}
                                    onClick={() => toggleRow(index)}
                                >
                                    <div className="flex items-center gap-2">
                                        <svg
                                            className={`w-4 h-4 transition-transform duration-200 text-text-muted flex-shrink-0
                                                ${expandedRows.includes(index) ? 'transform rotate-90' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="m9 18 6-6-6-6"
                                            />
                                        </svg>
                                        <span
                                            className="font-medium truncate"
                                            title={`${line.ID} - ${line.Item?.Name || line.Item?.Description || ''}`}
                                        >
                                            {`${line.ID} - ${line.Item?.Name || line.Item?.Description || ''}`}
                                        </span>
                                    </div>
                                    <div className="text-right" title={`${typeof line.InvoicedQuantity === 'object' ? line.InvoicedQuantity['#text'] : line.InvoicedQuantity} ${(typeof line.InvoicedQuantity === 'object' && line.InvoicedQuantity['@_unitCode']) ? line.InvoicedQuantity['@_unitCode'] : ''}`}>
                                        {typeof line.InvoicedQuantity === 'object' ? line.InvoicedQuantity['#text'] : line.InvoicedQuantity}
                                    </div>
                                    <div className="text-right" title={formatCurrency(typeof line.Price?.PriceAmount === 'object' ? line.Price.PriceAmount['#text'] : line.Price?.PriceAmount || 0)}>
                                        {formatCurrency(typeof line.Price?.PriceAmount === 'object' ? line.Price.PriceAmount['#text'] : line.Price?.PriceAmount || 0)}
                                    </div>
                                    <div className="text-right font-medium" title={formatCurrency(typeof line.LineExtensionAmount === 'object' ? line.LineExtensionAmount['#text'] : line.LineExtensionAmount || 0)}>
                                        {formatCurrency(typeof line.LineExtensionAmount === 'object' ? line.LineExtensionAmount['#text'] : line.LineExtensionAmount || 0)}
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {expandedRows.includes(index) && (
                                    <div className="md-card-content border-t border-border-secondary bg-bg-secondary/40 space-y-2 overflow-x-hidden md-scrollbar elevation-1 rounded-b-md">
                                        {/* Basic Information */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.ID.label',
                                                'invoiceFields.InvoiceLine.ID.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">{line.ID}</span>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={line.ID}
                                                    onChange={(e) => onDataChange?.(`InvoiceLines.${index}.ID`, e.target.value)}
                                                    className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                />
                                            )}
                                        </div>

                                        {/* Add Name field before Description */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.Name.label',
                                                'invoiceFields.InvoiceLine.Name.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">{line.Item?.Name || ''}</span>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={line.Item?.Name || ''}
                                                    onChange={(e) => onDataChange?.(`InvoiceLines.${index}.Item.Name`, e.target.value)}
                                                    className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                />
                                            )}
                                        </div>

                                        {/* Description field (existing) */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.Description.label',
                                                'invoiceFields.InvoiceLine.Description.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">{line.Item?.Description || ''}</span>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={line.Item?.Description || ''}
                                                    onChange={(e) => onDataChange?.(`InvoiceLines.${index}.Item.Description`, e.target.value)}
                                                    className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                />
                                            )}
                                        </div>

                                        {/* Add Note field with textarea */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.Note.label',
                                                'invoiceFields.InvoiceLine.Note.description'
                                            )}
                                            {readOnly ? (
                                                <div className="readonly-field px-3 py-2 rounded whitespace-pre overflow-auto border border-border-primary md-scrollbar">
                                                    {line.Note || ''}
                                                </div>
                                            ) : (
                                                <textarea
                                                    value={line.Note || ''}
                                                    onChange={(e) => onDataChange?.(`InvoiceLines.${index}.Note`, e.target.value)}
                                                    className="px-2 py-1 rounded resize-none overflow-auto border border-border-primary focus:border-brand-primary transition-colors md-scrollbar"
                                                    rows={1}
                                                />
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.Quantity.label',
                                                'invoiceFields.InvoiceLine.Quantity.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">{line.InvoicedQuantity}</span>
                                            ) : (
                                                <input
                                                    type="number"
                                                    value={line.InvoicedQuantity}
                                                    onChange={(e) => onDataChange?.(`InvoiceLines.${index}.InvoicedQuantity`, e.target.value)}
                                                    className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                />
                                            )}
                                        </div>

                                        {/* Unit Code field */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.UnitCode.label',
                                                'invoiceFields.InvoiceLine.UnitCode.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {line.unitCode || ''}
                                                </span>
                                            ) : (
                                                <UnitCodeSelector
                                                    value={line.unitCode || ''}
                                                    onChange={(newValue) => onDataChange?.(`InvoiceLines.${index}.unitCode`, newValue)}
                                                    placeholder={t('invoiceFields.InvoiceLine.UnitCode.selectPlaceholder', { defaultValue: 'Select unit...' })}
                                                    disabled={readOnly}
                                                    showGermanNames={language === 'de'}
                                                    showSymbols={true}
                                                    className="w-full"
                                                    data-testid={`invoice-line-unit-${index}`}
                                                />
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.UnitPrice.label',
                                                'invoiceFields.InvoiceLine.UnitPrice.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {formatCurrency(line.Price?.PriceAmount || 0)}
                                                </span>
                                            ) : (
                                                <CurrencyInput
                                                    id={`unit-price-${index}`}
                                                    name={`unit-price-${index}`}
                                                    placeholder="0,00"
                                                    defaultValue={line.Price?.PriceAmount || 0}
                                                    decimalsLimit={2}
                                                    decimalSeparator={separators.decimalSeparator}
                                                    groupSeparator={separators.groupSeparator}
                                                    onValueChange={(value) => {
                                                        onDataChange?.(`InvoiceLines.${index}.Price.PriceAmount`, value || '0');
                                                    }}
                                                    className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                    disabled={readOnly}
                                                    intlConfig={{ locale: language, currency: invoiceData.DocumentCurrencyCode || 'EUR' }}
                                                    allowNegativeValue={false}
                                                />
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.NetAmount.label',
                                                'invoiceFields.InvoiceLine.NetAmount.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {formatCurrency(line.LineExtensionAmount || 0)}
                                                </span>
                                            ) : (
                                                <CurrencyInput
                                                    id={`net-amount-${index}`}
                                                    name={`net-amount-${index}`}
                                                    placeholder="0,00"
                                                    defaultValue={line.LineExtensionAmount || 0}
                                                    decimalsLimit={2}
                                                    decimalSeparator={separators.decimalSeparator}
                                                    groupSeparator={separators.groupSeparator}
                                                    onValueChange={(value) => {
                                                        onDataChange?.(`InvoiceLines.${index}.LineExtensionAmount`, value || '0');
                                                    }}
                                                    className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                    disabled={readOnly}
                                                    intlConfig={{ locale: language, currency: invoiceData.DocumentCurrencyCode || 'EUR' }}
                                                />
                                            )}
                                        </div>

                                        {/* Tax Category ID field */}
                                        <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                            {renderLineItemLabelWithInfo(
                                                'invoiceFields.InvoiceLine.TaxCategory.label',
                                                'invoiceFields.InvoiceLine.TaxCategory.description'
                                            )}
                                            {readOnly ? (
                                                <span className="readonly-field px-3 py-2 rounded border border-border-primary">
                                                    {line.Item?.ClassifiedTaxCategory?.ID || ''}
                                                </span>
                                            ) : (
                                                <TaxCategorySelector
                                                    value={line.Item?.ClassifiedTaxCategory?.ID || ''}
                                                    onChange={(newValue) => onDataChange?.(`InvoiceLines.${index}.Item.ClassifiedTaxCategory.ID`, newValue)}
                                                    placeholder={t('invoiceFields.InvoiceLine.TaxCategory.selectPlaceholder', { defaultValue: 'Select tax category...' })}
                                                    disabled={readOnly}
                                                    showGermanNames={language === 'de'}
                                                    className="w-full"
                                                    data-testid={`invoice-line-tax-category-${index}`}
                                                />
                                            )}
                                        </div>

                                        {/* VAT Rate field */}
                                        {line.Item?.ClassifiedTaxCategory && (
                                            <div className="grid grid-cols-2 gap-4 py-2 border-b border-border-secondary">
                                                {renderLineItemLabelWithInfo(
                                                    'invoiceFields.InvoiceLine.VATRate.label',
                                                    'invoiceFields.InvoiceLine.VATRate.description'
                                                )}
                                                {readOnly ? (
                                                    <span className="readonly-field px-3 py-2 rounded border border-border-primary">{`${line.Item.ClassifiedTaxCategory.Percent}%`}</span>
                                                ) : (
                                                    <input
                                                        type="text"
                                                        value={line.Item.ClassifiedTaxCategory.Percent}
                                                        onChange={(e) => {
                                                            const numericValue = e.target.value.replace(/[^0-9.,]/g, '');
                                                            const parsedValue = parseFloat(numericValue.replace(',', '.')) || 0;
                                                            onDataChange?.(`InvoiceLines.${index}.Item.ClassifiedTaxCategory.Percent`, parsedValue);
                                                        }}
                                                        className="px-2 py-1 rounded border border-border-primary focus:border-brand-primary transition-colors"
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {/* Delete Button */}
                                        {!readOnly && onDeleteInvoiceLine && (
                                            <div className="flex justify-end mt-4">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation(); // Prevent toggling the row
                                                        onDeleteInvoiceLine(index);
                                                    }}
                                                    className="flex items-center gap-2 text-[var(--error)] elevation-1 hover:elevation-2 active:elevation-3 bg-[var(--error)]/10 border border-[var(--error)] rounded-md px-3 py-1.5"
                                                    title={t('invoice.buttons.deleteLine')}
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    {t('invoice.buttons.deleteLine') || 'Delete Line'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // No longer need scrollbarClass as we're using the md-scrollbar class directly

    // Render validation summary panel
    const renderValidationSummary = () => {
        if (!showValidation || !validationResult) return null;

        const { errors, warnings, info } = validationResult;
        const totalIssues = errors.length + warnings.length + info.length;

        if (totalIssues === 0) {
            return (
                <div className="mb-6 p-4 bg-green-50 border border-success rounded-lg">
                    <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-success" />
                        <span className="text-success font-medium">
                            {t('validation.allValid') || 'All fields are valid'}
                        </span>
                    </div>
                </div>
            );
        }

        return (
            <div className="mb-6 p-4 bg-bg-secondary dark:bg-bg-tertiary border border-error rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="w-5 h-5 text-error" />
                    <span className="text-error font-medium">
                        {t('validation.issues') || 'Validation Issues'}
                    </span>
                    {isValidating && (
                        <span className="text-sm text-text-secondary ml-auto">
                            {t('validation.validating') || 'Validating...'}
                        </span>
                    )}
                </div>
                
                {errors.length > 0 && (
                    <div className="mb-3">
                        <h4 className="text-error font-medium mb-2 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {t('validation.errors') || 'Errors'} ({errors.length})
                        </h4>
                        <ul className="space-y-1">
                            {errors.map((error, index) => (
                                <li key={index} className="text-text-primary dark:text-text-primary text-sm">
                                    • {translateValidationMessage(error.message)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {warnings.length > 0 && (
                    <div className="mb-3">
                        <h4 className="text-warning font-medium mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            {t('validation.warnings') || 'Warnings'} ({warnings.length})
                        </h4>
                        <ul className="space-y-1">
                            {warnings.map((warning, index) => (
                                <li key={index} className="text-text-primary dark:text-text-primary text-sm">
                                    • {translateValidationMessage(warning.message)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {info.length > 0 && (
                    <div>
                        <h4 className="text-info font-medium mb-2 flex items-center gap-1">
                            <Info className="w-4 h-4" />
                            {t('validation.info') || 'Info'} ({info.length})
                        </h4>
                        <ul className="space-y-1">
                            {info.map((item, index) => (
                                <li key={index} className="text-black dark:text-black text-sm">
                                    • {item.message}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // Render tax breakdown section separately
    const renderTaxBreakdown = () => {
        const taxSubtotals = (Array.isArray(invoiceData.TaxSubtotals) ? invoiceData.TaxSubtotals : []) as Array<{
            TaxableAmount: number;
            TaxAmount: number;
            TaxCategory: {
                ID: string;
                Percent: number;
                TaxScheme: {
                    ID: string;
                };
            };
        }>;

        return (
            <div className="mb-8">
                <h2 className="md-title-large text-text-primary mb-4 border-l-4 border-brand-primary pl-3 md-padding-2 bg-brand-primary/10 rounded-r-md shadow-sm dark:border-brand-primary">
                    {t('invoiceFields.TaxSubtotals.label')}
                </h2>
                <div className="md-card md-card-2 md-padding-4 overflow-x-hidden border border-border-secondary rounded-lg">
                    <TaxSubtotalsDisplay
                        subtotals={taxSubtotals}
                        readOnly={readOnly}
                        onChange={(newSubtotals) => handleFieldChange('TaxSubtotals', newSubtotals)}
                        onAddSubtotal={() => {
                            const newSubtotal = {
                                TaxableAmount: 0,
                                TaxAmount: 0,
                                TaxCategory: {
                                    ID: 'S',
                                    Percent: 19,
                                    TaxScheme: {
                                        ID: 'VAT'
                                    }
                                }
                            };
                            handleFieldChange('TaxSubtotals', [...taxSubtotals, newSubtotal]);
                        }}
                        onDeleteSubtotal={(index) => {
                            const newSubtotals = taxSubtotals.filter((_, i) => i !== index);
                            handleFieldChange('TaxSubtotals', newSubtotals);
                        }}
                        currency={invoiceData.DocumentCurrencyCode || 'EUR'}
                        totalTaxAmount={Number(invoiceData['Tax.Amount']) || 0}
                        showValidation={showValidation}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="md-scrollbar overflow-y-auto pr-2">
            <div className="space-y-8">
                {renderValidationSummary()}
                {renderInvoiceLines()}
                <div className="mt-4">
                    <button
                        onClick={onAddInvoiceLine}
                        className="flex items-center gap-2 elevation-1 hover:elevation-2 active:elevation-3 bg-[var(--brand-primary)] text-[var(--text-button)] px-4 py-2 rounded-md shadow-sm"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        {t('invoice.buttons.addLine')}
                    </button>
                </div>
                {Object.entries(fieldSections).map(([section, fields]) => (
                    <div key={section}>
                        <h2 className="md-title-large text-text-primary mb-4 border-l-4 border-brand-primary pl-3 md-padding-2 bg-brand-primary/10 rounded-r-md shadow-sm">{t(section)}</h2>
                        <div className="md-card md-card-2 md-padding-4 mb-8 overflow-x-hidden border border-border-secondary rounded-lg">
                            <div className="space-y-2">
                                {fields.filter(field => field.key !== 'TaxSubtotals').map(field => renderField(field))}
                            </div>
                        </div>
                        {/* Render Tax Breakdown after the tax section */}
                        {section === 'sections.tax' && renderTaxBreakdown()}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default InvoiceViewer;
